import React, { useMemo } from 'react';

import { Box, type BoxProps } from '@frontend/legacy-components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOption, CascaderCommonProps } from '../types';

export interface SearchWrapperProps<OptionType extends CascaderOption>
  extends BoxProps {
  searchWrapperRef?: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

export const InternalSearchWrapper = <OptionType extends CascaderOption>({
  children,
  searchWrapperRef,
}: {
  /** The children to be rendered. */
  children: React.ReactNode;
  searchWrapperRef?: React.RefObject<HTMLDivElement>;
}) => {
  const { cascaderStyles, getCommonProps } = useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomSearchWrapper = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.SEARCH_WRAPPER),
    [getComponent],
  );

  return (
    <CustomSearchWrapper
      getCommonProps={getCommonProps}
      __css={cascaderStyles.searchWrapper}
      searchWrapperRef={searchWrapperRef}
      className="cascader-search-wrapper"
    >
      {children}
    </CustomSearchWrapper>
  );
};

export const SearchWrapper = <OptionType extends CascaderOption>({
  children,
  getCommonProps: _,
  searchWrapperRef,
  ...rest
}: SearchWrapperProps<OptionType>) => {
  return (
    <Box {...rest} ref={searchWrapperRef}>
      {children}
    </Box>
  );
};
